import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography, Slider } from '@mui/material';
import { useTheme, styled } from "@mui/material/styles";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";

import useTableInstance from "hooks/useTableInstance";
import useDataSourceManager from "hooks/useDataSourceManager";

import CollapseCard from "components/Collapse";
import TableField from "components/Table";
import CategoricalBarChart from 'components/CategoricalBarChart';
import EllipsisTooltipCell from "utils/ellipsisTooltipCell";
import GlobalAlert from "components/Notification";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";

import Api from "pages/AInsight/Operations/util/api";

import icn_linebar_chart from 'assets/images/icons/icn_linebar_chart.png';

const DEFAULT_DISPLAY_COUNT = 2;
const checkMockup = process.env.REACT_APP_IS_MOCKUP;

const PrettoSlider = styled(Slider)({
  color: '#172664',
  height: 4,
  '& .MuiSlider-track': {
    border: 'none',
  },
  '& .MuiSlider-rail': {
    opacity: 1,
    backgroundColor: '#E2E8F0',
  },
  '& .MuiSlider-thumb': {
    height: 18,
    width: 18,
    backgroundColor: '#fff',
    border: '3px solid currentColor',
    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: 'inherit',
    },
    '&:before': {
      display: 'none',
    },
  },
  '& .MuiSlider-valueLabel': {
    display: 'none',
  },
});

const BottomActionBar = ({ parentId, show, onCheckFormValid, onCancel, onApply }) => {
  useEffect(() => {
    const parentElement = document.getElementById(parentId);
    const bottomActionBarElement = document.getElementById("bottomActionBar");

    if (parentElement && bottomActionBarElement) {
      const resizeObserver = new ResizeObserver(() => {
        const parentWidth = parentElement.clientWidth;
        bottomActionBarElement.style.width = `${parentWidth - 32}px`;
      });
      resizeObserver.observe(parentElement);

      const parentWidth = parentElement.clientWidth;
      bottomActionBarElement.style.width = `${parentWidth - 32}px`;

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [parentId]);

  useEffect(() => {
    const bottomActionBarElement = document.getElementById("bottomActionBar");
    if (!bottomActionBarElement) return;
    if (show) {
      bottomActionBarElement.style.transform = "translateY(20px)";
      bottomActionBarElement.style.transition = "transform 0.3s ease-in-out";
    } else {
      bottomActionBarElement.style.transform = "translateY(80px)";
    }
  }, [show]);

  return (
    <Box
      id="bottomActionBar"
      sx={{
        position: "fixed",
        bottom: "20px",
        width: 0,
        display: show ? "flex" : "none",
        justifyContent: "center",
        gap: 2,
        margin: "16px 0",
        padding: "16px",
        backgroundColor: "primary.lightBlue",
        transform: "translateY(80px)",
      }}
    >
      <OutlineButton
        onClick={onCancel}
        sx={{ marginRight: "20px" }}
        width="100px"
        height="32px"
      >
        Cancel
      </OutlineButton>
      <SolidButton
        onClick={onApply}
        width="100px"
        height="32px"
        disabled={!onCheckFormValid()}
      >
        Apply
      </SolidButton>
    </Box>
  );
};

const SystemPerformanceDrawer = ({ chartData, auth, onSuccess }) => {
  const theme = useTheme();
  const { sendBatch, sendRequest, dataMap, getData } = useDataSourceManager();
  const columnHelper = useMemo(() => createColumnHelper(), []);
  const showEditGeneral = useMemo(() => auth?.permissions !== 'Viewer', [auth]);

  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [threshold, setThreshold] = useState(chartData?.threshold || 70);
  const [originThreshold, setOriginThreshold] = useState(chartData?.threshold || 70);
  const [deviceData, setDeviceData] = useState([]);
  const [requestUuids] = useState({
    devices: uuidv4(),
    updateThreshold: uuidv4(),
  });
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "",
    message: "",
  });

  const fetchData = async () => {
    if (!auth && checkMockup !== 'true') return;
    setLoading(true);
    const api = Api.init(auth || {});
    const devicesParams = api.getSystemBottleneckDevicesParams();

    const { getSendBatchData } = await sendBatch([
      { ...devicesParams, requestUuid: requestUuids.devices },
    ]);

    const devicesRes = getSendBatchData({ requestUuid: requestUuids.devices })?.data;
    if (devicesRes?.devices) {
      setDeviceData(devicesRes.devices);
    } else if (devicesRes?.data?.devices) {
      setDeviceData(devicesRes.data.devices);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [auth]);

  useEffect(() => {
    if (chartData?.threshold !== undefined) {
      setThreshold(chartData.threshold);
      setOriginThreshold(chartData.threshold);
    }
  }, [chartData?.threshold]);

  useEffect(() => {
    const updateResponse = getData(requestUuids.updateThreshold);
    if (updateResponse) {
      if (updateResponse.hasOwnProperty("code") && updateResponse?.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: updateResponse?.message || "Failed to update threshold",
        });
        setLoading(false);
      } else {
        setOriginThreshold(threshold);
        setLoading(false);
        if (onSuccess) {
          onSuccess();
        }
      }
    }
  }, [dataMap, requestUuids.updateThreshold, getData, threshold, onSuccess]);

  const handleApply = () => {
    setLoading(true);
    const api = Api.init(auth || {});
    const { apiKey, restfulPlaceholders, params } = api.getSystemBottleneckThresholdUpdateParams(threshold);
    sendRequest(
      apiKey,
      requestUuids.updateThreshold,
      restfulPlaceholders,
      params,
    );
  };

  const handleCancel = () => {
    setThreshold(originThreshold);
  };

  const tableData = useMemo(() =>
    showAll ? deviceData : deviceData.slice(0, DEFAULT_DISPLAY_COUNT),
    [showAll, deviceData]);

  const columns = useMemo(() => [
    columnHelper.accessor("deviceName", {
      header: "Device Name",
      cell: (info) => <EllipsisTooltipCell text={info.getValue()} />,
    }),
    columnHelper.accessor("MaxCpuUsage", {
      header: "CPU",
      cell: (info) => (
        <Typography sx={{ color: '#172664', fontSize: '13px' }}>
          {info.getValue()}%
        </Typography>
      ),
    }),
    columnHelper.accessor("MaxMemoryUsage", {
      header: "RAM",
      cell: (info) => (
        <Typography sx={{ color: '#172664', fontSize: '13px' }}>
          {info.getValue()}%
        </Typography>
      ),
    }),
  ], [columnHelper]);

  const table = useTableInstance({
    tableName: "devicePerformanceTable",
    columns: columns,
    data: tableData,
    enableSorting: false,
    enablePagination: false,
  });

  const categoricalChartData = useMemo(() => {
    if (!chartData || !chartData.series) return null;
    return {
      categories: ['10%', '20%', '30%', '40%', '50%', '60%', '70%', '80%', '90%', '100%'],
      seriesData: chartData.series.map((val) => ({
        value: val,
        itemStyle: { color: '#4C6EF5' }
      }))
    };
  }, [chartData]);

  const tooltipOption = useMemo(() => ({
    show: true,
    trigger: 'item',
    backgroundColor: 'rgba(55, 55, 55, 0.9)',
    borderColor: 'transparent',
    padding: [8, 12],
    borderRadius: 4,
    textStyle: { color: '#fff' },
    formatter: (params) => {
      return `
        <div style="font-size: 12px; line-height: 1.6;">
          <div style="opacity: 0.9;">Load Level: ${params.name}</div>
          <div>Device Count : <span style="font-weight: bold;">${params.value}</span></div>
        </div>
      `;
    }
  }), []);

  const showMore = deviceData.length > DEFAULT_DISPLAY_COUNT;
  const containerId = "systemPerformanceContainer";

  return (
    <Box id={containerId} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3, position: 'relative' }}>
      {/* Title Section */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box component="img" src={icn_linebar_chart} sx={{ width: '20px', height: '20px', mr: 1.5 }} />
        <Typography sx={{ fontWeight: 'bold', color: '#172664', fontSize: '18px' }}>
          System Performance Bottleneck
        </Typography>
      </Box>

      {/* Utilization Trend Card */}
      <CollapseCard title="Resource Utilization Trend">
        <Box sx={{ width: "100%", pt: 2 }}>
          {categoricalChartData ? (
            <Box sx={{ position: 'relative' }}>
              <CategoricalBarChart chartData={categoricalChartData} height="200px" tooltipCustomOption={tooltipOption} />
            </Box>
          ) : (
            <Typography>No data available</Typography>
          )}

          <Box sx={{ px: 2, mt: 3, mb: 1, position: 'relative', zIndex: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#1C1C1E' }}>Config:</Typography>
                <Typography sx={{ fontSize: '13px', color: '#1C1C1E' }}>High CPU / Memory Threshold</Typography>
              </Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#172664' }}>
                {threshold}%
              </Typography>
            </Box>
            <PrettoSlider
              value={threshold}
              min={0}
              max={100}
              step={1}
              disabled={!showEditGeneral}
              onChange={(e, newValue) => setThreshold(newValue)}
            />
          </Box>
        </Box>
      </CollapseCard>

      {/* Top Consumers Table */}
      <CollapseCard title="Performance Impacted Device(Last 24 Hours)">
        <Box sx={{ width: "100%" }}>
          <TableField {...table} loading={loading} />
          {showMore && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <Typography
                onClick={() => setShowAll(prev => !prev)}
                variant="text"
                sx={{
                  minWidth: 0, padding: 0, color: theme.palette.primary.greySecound, cursor: "pointer",
                }}
              >
                {showAll ? "Show Less" : "More..."}
              </Typography>
            </Box>
          )}
        </Box>
      </CollapseCard>

      <BottomActionBar
        parentId={containerId}
        show={showEditGeneral && threshold !== originThreshold}
        onCheckFormValid={() => true}
        onCancel={handleCancel}
        onApply={handleApply}
      />

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default SystemPerformanceDrawer;
